import React, {forwardRef, useMemo, useReducer, useImperativeHandle} from 'react';

import {Attachments, ContentCard, ContentToolbar, NoIntegration, NoIntegrationCallout} from '../components';
import {createDefaultContent} from './utils';
import {
  useCreateBlock,
  useExecuteRequest,
  useGetIntegrations,
  useInitBlock,
  useGetResult,
  useUpdateBlockContentJson,
} from '../hooks';
import {BaseBlock, ContentOptions, ContentActions} from '../types';
import {defaultContentOptions, defaultContentActions, BLOCK_TYPE} from '../constants';
import {integrationToSelectOption} from '../utils';
import {contentReducer, setContentFormKeyValue} from '../store';
import {initialGDriveContentState} from './store';
import Key from './Key';
import Result from './Result';

import './GDrive.scss';

interface GDriveProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const GDrive = forwardRef(({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: GDriveProps, ref: any) => {
  const [contentState, contentDispatch] = useReducer(contentReducer, initialGDriveContentState);

  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const uid = content.uid;
  const {result} = useGetResult(uid, content.result_id, options.loadResult);

  const {integrations, hasIntegrations} = useGetIntegrations(content.type);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  const {executing, executeRequest} = useExecuteRequest(uid);

  useCreateBlock(content, createDefaultContent());
  useInitBlock(uid, content, contentDispatch);

  useImperativeHandle(ref, () => ({
    setAttachments(attachments: string[]) {
      /*
       * TODO: remove this and find a better logic to update attachments
       *
       * Attachments can come from an outer source (notebook, report) as they're calculated based on the available
       *   content in the outer source.
       */
      onAttachmentsChange(attachments);
    }
  }));

  const onExecute = () => {
    const block: any = {
      content_json: {
        key: contentState.form.key,
        integration_id: contentState.form.integration_id,
        attachments: contentState.form.attachments,
      }
    };
    executeRequest(block);
  };

  const onIntegrationIdChange = (integrationId: string) => {
    contentDispatch(setContentFormKeyValue('integration_id', integrationId));
    updateBlockContentJson('integration_id', integrationId);
  };

  const onKeyChange = (value: string) => {
    contentDispatch(setContentFormKeyValue('key', value));
    updateBlockContentJson('key', value);
  };

  const onAttachmentsChange = (attachments: string[]) => {
    contentDispatch(setContentFormKeyValue('attachments', attachments));
    updateBlockContentJson('attachments', attachments);
  };

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? uid : null}
      disabled={!hasIntegrations || options.readOnly}
      noContentElement={hasIntegrations ? null : <NoIntegration
          contentId={uid as string}
          type={BLOCK_TYPE.GDRIVE}
          showDelete={options.showDelete}
        />
      }
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
      showFooter={options.showFooter}
    >
      <div className="content__gdrive">
        <ContentToolbar
          contentId={uid as string}
          executing={executing}
          onExecute={options.showRun ? onExecute : null}
          selected={contentState.form.integration_id}
          onSelect={onIntegrationIdChange}
          options={integrations.map(integrationToSelectOption)}
          updating={updating}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={false} /* options.showSave */
          disabled={options.readOnly}
        />
        <div className="content-card__content__form">
          {
            !contentState.form.integration_id ? <NoIntegrationCallout/> : (
              <>
                <div className="gdrive-form">
                  <Key
                    defaultValue={contentState.form.key || ''}
                    onChange={onKeyChange}
                    disabled={options.readOnly || false}
                  />
                  {
                    options.showAttachments && (
                      <Attachments
                        value={contentState.form.attachments}
                        onChange={onAttachmentsChange}
                        disabled={options.readOnly}
                      />
                    )
                  }
                </div>
                {
                  options.showResult && (
                    <Result
                      key={content.type + '_' + content.id}
                      result={result}
                    />
                  )
                }
              </>
            )
          }
        </div>
      </div>
    </ContentCard>
  );
});
